import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from './ToastContext';
import { useAuth } from '../auth/AuthProvider';

/** Ordered pipeline stages displayed in the widget. */
export const DEPLOY_STAGES = ['upload', 'analyze', 'dockerfile', 'build', 'deploy', 'keycloak', 'done'] as const;
export type DeployStage = typeof DEPLOY_STAGES[number];

/** Terminal states — the tracker stops polling when it sees one. */
const TERMINAL_STAGES: ReadonlySet<string> = new Set(['done', 'failed']);

export interface DeployTrack {
  slug: string;
  name: string;
  /** App URL the server returned (http:// or https:// depending on env). */
  appUrl: string;
  /** Immutable version number of this build. */
  version: number;
  /** Latest pipeline stage reported. */
  currentStage: DeployStage | 'failed';
  /** Raw event log for the build-log drawer. */
  events: Array<{ stage: string; at: string; message: string }>;
  startedAt: number;
  /** Populated when the build terminates. */
  finishedAt?: number;
  /** Friendly error summary when the pipeline fails. */
  errorMessage?: string;
}

interface DeployTrackerContextValue {
  tracks: DeployTrack[];
  startTrack: (input: { slug: string; name: string; appUrl: string; version: number }) => void;
  pushEvent: (slug: string, event: { stage: string; at: string; message: string }) => void;
  markSucceeded: (slug: string) => void;
  markFailed: (slug: string, message: string) => void;
  dismiss: (slug: string) => void;
}

const DeployTrackerContext = createContext<DeployTrackerContextValue | null>(null);

/** Hook accessor for the deploy tracker. */
export function useDeployTracker(): DeployTrackerContextValue {
  const ctx = useContext(DeployTrackerContext);
  if (!ctx) throw new Error('useDeployTracker must be used inside <DeployTrackerProvider>');
  return ctx;
}

/**
 * Provider for in-flight deploys. Lives at app root so the persistent widget
 * survives route changes. Fires a toast when a track reaches a terminal
 * state — the toast is routed to whichever page the user happens to be on.
 */
export function DeployTrackerProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [tracks, setTracks] = useState<DeployTrack[]>([]);
  const toast = useToast();
  const { token } = useAuth();
  const prevTracksRef = useRef<DeployTrack[]>([]);
  const abortersRef = useRef<Map<string, AbortController>>(new Map());

  // Fire toasts ONCE per terminal transition. Kept out of the setTracks
  // updater so we never call setState-on-other-component during render.
  useEffect(() => {
    for (const track of tracks) {
      const prev = prevTracksRef.current.find(p => p.slug === track.slug);
      const prevStage = prev?.currentStage;
      if (prevStage === track.currentStage) continue;
      if (track.currentStage === 'done' && prevStage !== 'done') {
        toast.push({
          variant: 'success',
          title: 'App deployed',
          body: `${track.name} is live at ${track.appUrl}`,
          href: track.appUrl,
          timeoutMs: 8000,
        });
      } else if (track.currentStage === 'failed' && prevStage !== 'failed') {
        toast.push({
          variant: 'error',
          title: 'Deploy failed',
          body: `${track.name}: ${track.errorMessage ?? 'Unknown error'}`,
          timeoutMs: 10_000,
        });
      }
    }
    prevTracksRef.current = tracks;
  }, [tracks, toast]);

  const pushEvent = useCallback((slug: string, event: { stage: string; at: string; message: string }) => {
    setTracks(prev => prev.map(track => {
      if (track.slug !== slug) return track;
      const nextEvents = [...track.events, event];
      // `failed` and `done` are terminal stages driven from the server — carry
      // the message + finishedAt so the UI matches the stream without waiting
      // for the slower polling fallback.
      if (event.stage === 'failed') {
        return { ...track, events: nextEvents, currentStage: 'failed', errorMessage: event.message, finishedAt: Date.now() };
      }
      if (event.stage === 'done') {
        return { ...track, events: nextEvents, currentStage: 'done', finishedAt: Date.now() };
      }
      const nextStage = isKnownStage(event.stage) ? event.stage : track.currentStage;
      return { ...track, events: nextEvents, currentStage: nextStage };
    }));
  }, []);

  const startTrack = useCallback((input: { slug: string; name: string; appUrl: string; version: number }) => {
    setTracks(prev => {
      if (prev.some(t => t.slug === input.slug)) return prev;
      return [...prev, {
        slug: input.slug, name: input.name,
        appUrl: input.appUrl, version: input.version,
        currentStage: 'upload',
        events: [],
        startedAt: Date.now(),
      }];
    });
    startEventStream(input.slug, input.version, token, pushEvent, abortersRef.current);
  }, [token, pushEvent]);

  const markSucceeded = useCallback((slug: string) => {
    setTracks(prev => prev.map(track =>
      track.slug === slug && !TERMINAL_STAGES.has(track.currentStage)
        ? { ...track, currentStage: 'done', finishedAt: Date.now() }
        : track,
    ));
    closeStream(slug, abortersRef.current);
  }, []);

  const markFailed = useCallback((slug: string, message: string) => {
    setTracks(prev => prev.map(track =>
      track.slug === slug && !TERMINAL_STAGES.has(track.currentStage)
        ? { ...track, currentStage: 'failed', finishedAt: Date.now(), errorMessage: message }
        : track,
    ));
    closeStream(slug, abortersRef.current);
  }, []);

  const dismiss = useCallback((slug: string) => {
    setTracks(prev => prev.filter(t => t.slug !== slug));
    closeStream(slug, abortersRef.current);
  }, []);

  const value = useMemo<DeployTrackerContextValue>(
    () => ({ tracks, startTrack, pushEvent, markSucceeded, markFailed, dismiss }),
    [tracks, startTrack, pushEvent, markSucceeded, markFailed, dismiss],
  );

  return <DeployTrackerContext.Provider value={value}>{children}</DeployTrackerContext.Provider>;
}

const KNOWN_STAGES: ReadonlySet<string> = new Set(DEPLOY_STAGES);

/** Narrow an arbitrary stage string to a known DeployStage. */
function isKnownStage(stage: string): stage is DeployStage {
  return KNOWN_STAGES.has(stage);
}

/** SSE line prefix used by the backend build-log endpoint. */
const SSE_DATA_PREFIX = 'data: ';

/**
 * Open an SSE connection to the per-build log stream and forward each
 * event into the tracker's `pushEvent`. The returned AbortController is
 * stashed per-slug so we can close the connection on terminal state.
 */
function startEventStream(
  slug: string,
  version: number,
  token: string,
  onEvent: (slug: string, event: { stage: string; at: string; message: string }) => void,
  aborters: Map<string, AbortController>,
): void {
  closeStream(slug, aborters);
  const controller = new AbortController();
  aborters.set(slug, controller);
  void consumeStream(slug, version, token, onEvent, controller.signal);
}

/** Abort an in-flight stream for a slug if present, then forget it. */
function closeStream(slug: string, aborters: Map<string, AbortController>): void {
  const controller = aborters.get(slug);
  if (!controller) return;
  controller.abort();
  aborters.delete(slug);
}

/** Pump the SSE endpoint, parsing each `data:` line as a build event. */
async function consumeStream(
  slug: string,
  version: number,
  token: string,
  onEvent: (slug: string, event: { stage: string; at: string; message: string }) => void,
  signal: AbortSignal,
): Promise<void> {
  try {
    const response = await fetch(`/api/apps/${slug}/versions/${version}/logs`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!response.ok || !response.body) return;
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) return;
      const chunk = decoder.decode(value, { stream: true });
      for (const line of chunk.split('\n')) {
        if (!line.startsWith(SSE_DATA_PREFIX)) continue;
        const body = line.substring(SSE_DATA_PREFIX.length);
        try {
          const event = JSON.parse(body) as { stage: string; at: string; message: string };
          onEvent(slug, event);
        } catch {
          /* ignore malformed payloads */
        }
      }
    }
  } catch {
    /* network / abort errors are expected during close — swallow */
  }
}
